import { Component, OnInit, inject, DestroyRef, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { tap, catchError, finalize } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';

import { MachineModuleService } from '@shared/services/api/machine-module.service';
import { ThemeService } from '../services/theme.service';
import { MachineModuleStatusType, MODULE_REGISTRY } from '@shared/models/types';

@Component({
  selector: 'app-machine-modules-form',
  templateUrl: './machine-modules-form.component.html',
  styleUrl: './machine-modules-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MachineModulesFormComponent implements OnInit {
  private _destroyRef = inject(DestroyRef);
  private _cdr = inject(ChangeDetectorRef);
  private _themeService = inject(ThemeService);
  private _route = inject(ActivatedRoute);
  private _location = inject(Location);
  private _router = inject(Router);
  private _machineModuleService = inject(MachineModuleService);

  isLight = true;
  isLoading = false;
  isSaving = false;
  saveStatusMessage = '';
  saveStatusType: 'success' | 'error' = 'success';
  serialNumber: string | null = null;
  moduleRegistry = MODULE_REGISTRY;
  /** Server-confirmed state, used to detect pending changes and to revert on save failure. */
  private originalStatuses: MachineModuleStatusType[] = [];
  /** Local working copy: toggled in the UI but not yet persisted until "Salva" is clicked. */
  statuses: MachineModuleStatusType[] = [];

  ngOnInit(): void {
    this._themeService.isLight$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((isLight) => {
      this.isLight = isLight;
      this._cdr.markForCheck();
    });

    this._route.params.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((params) => {
      this.serialNumber = params['serialNumber'] || null;
      if (this.serialNumber) {
        this.loadStatuses();
      }
    });
  }

  private loadStatuses(): void {
    if (!this.serialNumber) return;
    this.isLoading = true;
    this._machineModuleService
      .getStatuses(this.serialNumber)
      .pipe(
        tap((statuses) => {
          this.originalStatuses = statuses.map((s) => ({ ...s }));
          this.statuses = statuses.map((s) => ({ ...s }));
          this.isLoading = false;
          this._cdr.markForCheck();
        }),
        catchError(() => {
          this.isLoading = false;
          this._cdr.markForCheck();
          return of(null);
        })
      )
      .subscribe();
  }

  isEnabled(moduleCode: string): boolean {
    return this.statuses.find((s) => s.moduleCode === moduleCode)?.enabled ?? true;
  }

  /** Toggles the module locally only — nothing is persisted until "Salva" is clicked. */
  onToggle(moduleCode: string, enabled: boolean): void {
    const status = this.statuses.find((s) => s.moduleCode === moduleCode);
    if (status) {
      status.enabled = enabled;
    } else {
      this.statuses.push({ moduleCode, enabled });
    }
    this.clearStatusMessage();
    this._cdr.markForCheck();
  }

  hasChanges(): boolean {
    return this.moduleRegistry.some((module) => this.isEnabled(module.code) !== this.wasEnabled(module.code));
  }

  private wasEnabled(moduleCode: string): boolean {
    return this.originalStatuses.find((s) => s.moduleCode === moduleCode)?.enabled ?? true;
  }

  onSubmit(): void {
    if (!this.serialNumber || this.isSaving || !this.hasChanges()) return;

    const changedModules = this.moduleRegistry.filter(
      (module) => this.isEnabled(module.code) !== this.wasEnabled(module.code)
    );

    this.isSaving = true;
    this.clearStatusMessage();

    forkJoin(
      changedModules.map((module) =>
        this._machineModuleService.setEnabled(this.serialNumber!, module.code, this.isEnabled(module.code))
      )
    )
      .pipe(
        tap((results) => {
          const latest = results[results.length - 1];
          this.originalStatuses = latest.map((s) => ({ ...s }));
          this.statuses = latest.map((s) => ({ ...s }));
          this.saveStatusMessage = 'MACHINE_MODULES.SAVE_SUCCESS';
          this.saveStatusType = 'success';
        }),
        catchError(() => {
          this.saveStatusMessage = 'MACHINE_MODULES.SAVE_ERROR';
          this.saveStatusType = 'error';
          return of(null);
        }),
        finalize(() => {
          this.isSaving = false;
          this._cdr.markForCheck();
        })
      )
      .subscribe();
  }

  clearStatusMessage(): void {
    this.saveStatusMessage = '';
  }

  goBack(): void {
    this._location.back();
  }

  goToList(): void {
    this._router.navigate(['/machine-modules']);
  }

  getYear(): number {
    return new Date().getFullYear();
  }
}
